/* ============================================================================
   PB DRAINAGE — Pool Busters
   Page behaviour for the Pool Drainage Solutions page.

   Deliberately small and dependency-free. Everything degrades:
   - no JS            → full page renders, FAQ still opens (native <details>)
   - reduced motion   → hero un-pins to a static, fully-labelled cross-section
   - old browsers     → feature-detected, silently skipped

   All work happens inside .pb-drainage. Nothing global is touched except one
   scroll and one resize listener, both passive.
   ========================================================================= */
(function () {
  'use strict';

  if (window.__pbDrainageInit) return;
  window.__pbDrainageInit = true;

  var ROOT_SEL = '.pb-drainage';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* --- tiny helpers ------------------------------------------------------ */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  /* 0 below `from`, 1 above `to`, smoothly eased between */
  function ramp(p, from, to) {
    if (to === from) return p >= to ? 1 : 0;
    var t = clamp((p - from) / (to - from), 0, 1);
    return t * t * (3 - 2 * t);
  }
  /* rises to 1 then falls back to 0 — used for the rain squall */
  function pulse(p, a, b, c, d) {
    return Math.min(ramp(p, a, b), 1 - ramp(p, c, d));
  }

  var raf = window.requestAnimationFrame
    ? window.requestAnimationFrame.bind(window)
    : function (fn) { return setTimeout(fn, 16); };

  var scrollTasks = [];
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    raf(function () {
      for (var i = 0; i < scrollTasks.length; i++) scrollTasks[i]();
      ticking = false;
    });
  }
  function addScrollTask(fn) {
    scrollTasks.push(fn);
    fn();
  }

  var resizeTasks = [];
  function addResizeTask(fn) { resizeTasks.push(fn); fn(); }

  /* =========================================================================
     1a. CINEMATIC CAMERA — canvas, replaces the layered scene when present
     A single pinned camera (center + zoom) pushes and pans through the same
     baked assets the layered scene already uses, per fable-hero-plan.md.
     It reads the hero's own eased progress (`cur`, below) so the picture and
     the captions move as one continuous journey rather than two clocks.

     Progressive enhancement only: while the small set of images is loading,
     or if Canvas2D isn't available, `.pbd-scene` (the existing layered
     scene) stays exactly as it was and this never turns on. `onReady` is
     `wake()`, passed in by initHero, so the first real frame paints the
     moment loading finishes even if the spring had already settled.
     ====================================================================== */
  function initCamera(hero, canvas, onReady) {
    var ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return null;

    /* Scene constants — mirror build/geometry.py. If that ever regenerates
       different numbers, update both places together. */
    var SCENE_W = 2400, SCENE_H = 1800;
    var CUT = 927, WT = 1138, GW_TRAVEL = 840;
    var LIFT_X = 610, LIFT_Y = 889, LIFT_W = 1179, LIFT_H = 311;

    /* Rain ripples on the pool surface — the same 24 ellipses and delays as
       snippets/pb-drainage-scene.liquid's g_drops (build/geometry.py), so
       the canvas and the layered-scene fallback show identical rings. Each
       [cx, cy, rx, ry, delaySeconds] in scene space; the ring animation
       below reproduces @keyframes pbd-ripple (2.1s, scale .15→1, opacity
       0→.85 at 12% then linear to 0) so this reads as the same weather,
       not a different effect layered on top. */
    var DROPS = [
      [1003, 897, 31.7, 10.8, 0.81], [1477, 892, 31.2, 10.6, 1.79],
      [812, 887, 30.7, 10.4, 0.44], [1281, 882, 30.1, 10.2, 1.14],
      [1632, 877, 29.6, 10.1, 0.99], [1140, 872, 29.1, 9.9, 1.51],
      [753, 867, 28.5, 9.7, 1.33], [1387, 862, 28.0, 9.5, 2.00],
      [1552, 857, 27.5, 9.3, 2.24], [937, 852, 26.9, 9.2, 2.11],
      [1209, 847, 26.4, 9.0, 0.13], [1650, 842, 25.9, 8.8, 1.66],
      [1085, 837, 25.3, 8.6, 2.42], [1332, 832, 24.8, 8.4, 2.52],
      [890, 827, 24.3, 8.3, 0.31], [1488, 822, 23.7, 8.1, 0.60],
      [1024, 818, 23.2, 7.9, 0.81], [1447, 813, 22.7, 7.7, 1.79],
      [854, 808, 22.1, 7.5, 0.44], [1272, 803, 21.6, 7.3, 1.14],
      [1585, 798, 21.1, 7.2, 0.99], [1146, 793, 20.5, 7.0, 1.51],
      [802, 788, 20.0, 6.8, 1.33], [1366, 783, 19.5, 6.6, 2.00]
    ];

    /* Camera keyframes: [p, cx, cy, ln z]. One push in, one pull back out —
       not a shot per corner. p lines up with the stage windows (EDGES,
       below) so the camera changes gear exactly when the copy does: dead
       flat through "The pool" AND "Rainfall" (K0→K1 both z=1 — the zoom
       only starts once rain has had its moment, not at frame one), tilt
       down as the cut reveals under "Below grade", push into the soil as
       the water table rises through "Groundwater" / "Pressure" — the move
       that reads as "watch the ground" — then pull back out through
       "Floating liner" so the whole lifted sheet is in frame, not a close
       crop of it. Revised 2026-08-14 twice: first pass cut the corner
       close-ups and added the pull-back; this pass delays the push until
       after the rain and retimes every point to the widened stage windows. */
    var KEYS = [
      [0.00, 1200,  905, 0.000], [0.17, 1200,  905, 0.000],
      [0.35, 1200, 1010, 0.247], [0.52, 1220, 1140, 0.668],
      [0.68, 1220, 1150, 0.742], [0.84, 1200, 1090, 0.372],
      [0.94, 1200, 1060, 0.077], [1.00, 1200,  950, 0.020]
    ];
    var KP = KEYS.map(function (k) { return k[0]; });

    /* Monotone cubic (Fritsch–Carlson) through the keyframes: C¹-smooth,
       zero overshoot. An overshooting camera slams into the edge clamp and
       reads as a bump — this is what keeps every move soft. */
    function monotoneTangents(ys) {
      var n = ys.length, dsec = [], m = new Array(n), i;
      for (i = 0; i < n - 1; i++) dsec.push((ys[i + 1] - ys[i]) / (KP[i + 1] - KP[i]));
      m[0] = dsec[0]; m[n - 1] = dsec[n - 2];
      for (i = 1; i < n - 1; i++) {
        m[i] = (dsec[i - 1] === 0 || dsec[i] === 0 || (dsec[i - 1] < 0) !== (dsec[i] < 0))
          ? 0 : (dsec[i - 1] + dsec[i]) / 2;
      }
      for (i = 0; i < n - 1; i++) {
        if (dsec[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
        var a = m[i] / dsec[i], b = m[i + 1] / dsec[i], s = a * a + b * b;
        if (s > 9) { var t = 3 / Math.sqrt(s); m[i] = t * a * dsec[i]; m[i + 1] = t * b * dsec[i]; }
      }
      return m;
    }
    function spline(ys) {
      var m = monotoneTangents(ys);
      return function (p) {
        p = clamp(p, KP[0], KP[KP.length - 1]);
        var i = 0;
        while (i < KP.length - 2 && p > KP[i + 1]) i++;
        var h = KP[i + 1] - KP[i], t = h ? (p - KP[i]) / h : 0, t2 = t * t, t3 = t2 * t;
        var h00 = 2 * t3 - 3 * t2 + 1, h10 = t3 - 2 * t2 + t, h01 = -2 * t3 + 3 * t2, h11 = t3 - t2;
        return h00 * ys[i] + h10 * h * m[i] + h01 * ys[i + 1] + h11 * h * m[i + 1];
      };
    }
    var camX = spline(KEYS.map(function (k) { return k[1]; }));
    var camY = spline(KEYS.map(function (k) { return k[2]; }));
    var camLZ = spline(KEYS.map(function (k) { return k[3]; }));

    /* --- assets: base plate, wet regrade, the pre-cut cover, six lift
       states + masks -------------------------------------------------------
       `cover` is the same "yard before it's cut open" photo the layered
       scene uses (pb-hero-cover.jpg) — drawn over the below-grade region and
       faded out as `reveal` ramps, so the opening shot reads as an ordinary
       backyard pool and the cutaway only appears once the story gets there. */
    var d = canvas.dataset;
    var base = new Image(), wet = new Image(), cover = new Image();
    base.src = d.base; wet.src = d.wet; cover.src = d.cover;
    var liftImgs = [], liftMaskImgs = [], liftReady = new Array(6);
    var i;
    for (i = 0; i < 6; i++) {
      var ci = new Image(), mi = new Image();
      ci.src = d['lift' + (i + 1)];
      mi.crossOrigin = 'anonymous';    /* only this one needs getImageData */
      mi.src = d['lift' + (i + 1) + 'a'];
      liftImgs.push(ci); liftMaskImgs.push(mi);
    }

    /* The mask files are luminance masks (the same ones the SVG <mask>
       fallback uses), not alpha channels, so lift the luminance into alpha
       with one pixel pass, then punch the colour frame down to that shape
       with destination-in. Done once per frame at load, not per scroll frame. */
    function compositeMask(colorImg, maskImg) {
      var c = document.createElement('canvas');
      c.width = LIFT_W; c.height = LIFT_H;
      var cx2 = c.getContext('2d');
      cx2.drawImage(colorImg, 0, 0, LIFT_W, LIFT_H);
      var mc = document.createElement('canvas');
      mc.width = LIFT_W; mc.height = LIFT_H;
      var mcx = mc.getContext('2d');
      mcx.drawImage(maskImg, 0, 0, LIFT_W, LIFT_H);
      try {
        var id = mcx.getImageData(0, 0, LIFT_W, LIFT_H), px = id.data, p2;
        for (p2 = 0; p2 < px.length; p2 += 4) px[p2 + 3] = px[p2];
        mcx.putImageData(id, 0, 0);
      } catch (e) { return null; }   /* tainted canvas — caller falls back */
      cx2.globalCompositeOperation = 'destination-in';
      cx2.drawImage(mc, 0, 0);
      return c;
    }

    var pending = 15, failed = false, ready = false;
    function onAsset(ok) {
      if (!ok) failed = true;
      pending--;
      if (pending > 0) return;
      if (failed) return;    /* stay in the layered-scene fallback */
      for (var k = 0; k < 6; k++) {
        liftReady[k] = compositeMask(liftImgs[k], liftMaskImgs[k]);
        if (!liftReady[k]) { failed = true; }
      }
      if (failed) return;
      ready = true;
      hero.classList.add('has-camera');
      canvas.classList.add('is-ready');
      onReady();
    }
    base.onload = function () { onAsset(true); }; base.onerror = function () { onAsset(false); };
    wet.onload = function () { onAsset(true); }; wet.onerror = function () { onAsset(false); };
    cover.onload = function () { onAsset(true); }; cover.onerror = function () { onAsset(false); };
    for (i = 0; i < 6; i++) {
      liftImgs[i].onload = function () { onAsset(true); };
      liftImgs[i].onerror = function () { onAsset(false); };
      liftMaskImgs[i].onload = function () { onAsset(true); };
      liftMaskImgs[i].onerror = function () { onAsset(false); };
    }

    /* --- sizing --------------------------------------------------------
       The backing store must match the *rendered* box or every drawImage is
       blurry and misaligned. A ResizeObserver is used rather than measuring
       once at script run, because the hero sits in a CSS grid whose size
       isn't resolved yet at that point. dpr capped at 1.6, same as the
       rest of the hero. */
    var vw = 0, vh = 0;
    function size() {
      var r = canvas.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 1.6);
      vw = r.width; vh = r.height;
      var bw = Math.round(vw * dpr), bh = Math.round(vh * dpr);
      if (canvas.width !== bw || canvas.height !== bh) { canvas.width = bw; canvas.height = bh; }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    if ('ResizeObserver' in window) { new ResizeObserver(size).observe(canvas); }
    else { addResizeTask(size); }
    size();

    /* --- draw ------------------------------------------------------------
       One camera transform; everything else is a source-rect crop against
       it (fable-hero-plan.md §4/§6). Patches are culled against the camera
       window before they're drawn. */
    function draw(cur) {
      if (!ready || !vw || !vh) return;
      var z = Math.exp(camLZ(cur.p));
      var s0 = Math.max(vw / SCENE_W, vh / SCENE_H), s = s0 * z;
      var winW = vw / s, winH = vh / s;
      var x0 = clamp(camX(cur.p) - winW / 2, 0, SCENE_W - winW);
      var y0 = clamp(camY(cur.p) - winH * 0.46, 0, SCENE_H - winH);

      /* no clearRect: the base plate is cover-fit, so it always paints every
         pixel of the canvas — an explicit clear would just be wasted work. */
      ctx.drawImage(base, x0, y0, winW, winH, 0, 0, vw, vh);

      /* wet soil — one continuous table line, not six steps: crop the wet
         plate from the current table row down, only where it's in view. */
      var table = clamp(WT + (1 - cur.gw) * GW_TRAVEL, CUT, SCENE_H);
      var visTop = Math.max(y0, table);
      if (visTop < y0 + winH && table < SCENE_H) {
        var srcY = visTop - CUT, srcH = (y0 + winH) - visTop;
        var destY = (visTop - y0) * s, destH = srcH * s;
        ctx.drawImage(wet, x0, srcY, winW, srcH, 0, destY, vw, destH);
        var gy = (table - y0) * s;
        if (gy > -20 && gy < vh + 20) {
          var grad = ctx.createLinearGradient(0, gy - 14, 0, gy + 14);
          grad.addColorStop(0, 'rgba(126,227,231,0)');
          grad.addColorStop(0.5, 'rgba(126,227,231,' + (0.22 * cur.gw).toFixed(3) + ')');
          grad.addColorStop(1, 'rgba(126,227,231,0)');
          ctx.fillStyle = grad;
          ctx.fillRect(0, gy - 14, vw, 28);
        }
      }

      /* the pre-cut cover — "The pool" opens as an ordinary backyard shot,
         not a cutaway, and only reveals the section as the story gets to
         "Below grade" (reveal ramps 0.28→0.52, same window that drives the
         layered-scene fallback, so both versions turn the same corner at
         the same scroll position). Cross-fades away rather than sliding, to
         keep this to one more source-rect crop instead of a second camera
         transform; culled entirely once fully revealed. */
      if (cur.reveal < 0.999) {
        var covTop = Math.max(y0, CUT);
        if (covTop < y0 + winH) {
          var csrcY = covTop - CUT, csrcH = (y0 + winH) - covTop;
          var cdestY = (covTop - y0) * s, cdestH = csrcH * s;
          ctx.globalAlpha = 1 - cur.reveal;
          ctx.drawImage(cover, x0, csrcY, winW, csrcH, 0, cdestY, vw, cdestH);
          ctx.globalAlpha = 1;
        }
      }

      /* liner lift — the same six-frame dissolve the CSS version uses,
         weighted so the pressure stage carries the first third of the
         movement (see .pbd-lyr--liner in pb-drainage.css). */
      var swell = cur.press * 0.34 + cur.lift * 0.66;
      var lx0 = Math.max(x0, LIFT_X), ly0 = Math.max(y0, LIFT_Y);
      var lx1 = Math.min(x0 + winW, LIFT_X + LIFT_W), ly1 = Math.min(y0 + winH, LIFT_Y + LIFT_H);
      if (lx1 > lx0 && ly1 > ly0) {
        for (var f = 0; f < 6; f++) {
          var op = clamp(swell * 6 - f, 0, 1);
          if (op <= 0.001) continue;
          ctx.globalAlpha = op;
          ctx.drawImage(liftReady[f],
            lx0 - LIFT_X, ly0 - LIFT_Y, lx1 - lx0, ly1 - ly0,
            (lx0 - x0) * s, (ly0 - y0) * s, (lx1 - lx0) * s, (ly1 - ly0) * s);
        }
        ctx.globalAlpha = 1;
      }

      /* weather — procedural, screen space, no extra textures. Time-based
         phase so the fall speed doesn't drift with frame rate. */
      if (cur.rain > 0.01) {
        var skyBot = Math.min(y0 + winH, CUT);
        if (skyBot > y0) {
          var sg = ctx.createLinearGradient(0, 0, 0, (skyBot - y0) * s);
          sg.addColorStop(0, 'rgba(10,26,38,' + (0.55 * cur.rain).toFixed(3) + ')');
          sg.addColorStop(1, 'rgba(10,26,38,0)');
          ctx.fillStyle = sg;
          ctx.fillRect(0, 0, vw, (skyBot - y0) * s);
        }
        ctx.strokeStyle = 'rgba(223,242,244,' + (0.5 * cur.rain).toFixed(3) + ')';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        var phase = performance.now() * 0.34;
        for (var r2 = 0; r2 < 70; r2++) {
          var rx = (r2 * 97 + phase * 6) % (vw + 60) - 30;
          var ry = (r2 * 53 + phase * 9) % (vh + 60) - 30;
          ctx.moveTo(rx, ry); ctx.lineTo(rx - 5, ry + 26);
        }
        ctx.stroke();

        /* ripples where the rain actually lands, not just streaks in the
           air — this is what reads as real weather on real water. */
        var now = performance.now() / 1000;
        ctx.strokeStyle = '#eafcff';
        for (var d = 0; d < DROPS.length; d++) {
          var drop = DROPS[d];
          var t = ((now + drop[4]) % 2.1) / 2.1;
          var dscale = 0.15 + 0.85 * t;
          var dop = t <= 0.12 ? (t / 0.12) * 0.85 : 0.85 * (1 - (t - 0.12) / 0.88);
          if (dop <= 0.01) continue;
          var dsx = (drop[0] - x0) * s, dsy = (drop[1] - y0) * s;
          if (dsx < -60 || dsx > vw + 60 || dsy < -60 || dsy > vh + 60) continue;
          ctx.globalAlpha = dop * cur.rain * 0.85;
          ctx.lineWidth = 2.4 * s;
          ctx.beginPath();
          ctx.ellipse(dsx, dsy, Math.max(0.1, drop[2] * dscale * s), Math.max(0.1, drop[3] * dscale * s), 0, 0, Math.PI * 2);
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
      }
    }

    return { draw: draw };
  }

  /* =========================================================================
     1. SCROLL-STORY HERO
     Six stages. Scroll position inside the tall track becomes a 0→1 progress
     value; five derived values drive the scene through CSS custom properties
     so the browser can composite them without touching the DOM per frame.
     ====================================================================== */
  function initHero(hero) {
    var track = $('.pbd-hero__track', hero);
    var pin = $('.pbd-hero__pin', hero);
    var svg = $('.pbd-hero__svg:not(.pbd-hero__svg--over)', hero);
    if (!track || !pin) return;

    var stages = $$('.pbd-hero__stage', hero);
    var ticks = $$('.pbd-hero__tick', hero);

    /* The photographic layers are SVGs with preserveAspectRatio="slice", which
       does its own arithmetic. The groundwater layer is plain HTML, so it needs
       the same number in pixels to line up with them: --pbd-u is how big one
       unit of the 2400x1800 canvas is right now. Written on resize only. */
    var scene = $('.pbd-scene', hero);
    function fitUnit() {
      if (!scene) return;
      var b = scene.getBoundingClientRect();
      if (!b.width || !b.height) return;
      scene.style.setProperty('--pbd-u', Math.max(b.width / 2400, b.height / 1800) + 'px');
    }
    addResizeTask(fitUnit);
    fitUnit();

    function fitScene() {
      if (!svg) return;
      var narrow = window.matchMedia('(max-width: 760px)').matches;
      svg.setAttribute('viewBox', narrow ? '-40 -20 1280 1060' : '0 0 1200 820');
    }
    addResizeTask(fitScene);

    /* Static end-state for reduced motion: show everything, pin nothing.
       Launch pass: phones get the same treatment. The pinned scrub was
       cropping badly and juddering on mobile, so under 760px the hero is the
       finished cutaway with the copy underneath — the story in one frame. */
    if (reduce.matches || window.matchMedia('(max-width: 760px)').matches) {
      ['p', 'reveal', 'gw', 'press', 'lift'].forEach(function (k) {
        hero.style.setProperty('--pbd-' + k, 1);
      });
      hero.style.setProperty('--pbd-rain', 0.35);
      stages.forEach(function (s) {
        s.style.setProperty('--pbd-o', 1);
        s.classList.add('is-lit');
      });
      ticks.forEach(function (t) { t.classList.add('is-on'); });
      return;
    }

    /* -----------------------------------------------------------------------
       The story is a set of overlapping curves, not a set of levels.

       Scroll gives a target; a spring runs every frame and eases the six
       drivers toward it. Wheel and trackpad deliver scroll in coarse jumps, so
       writing scroll position straight to the scene is what makes a pinned
       hero feel steppy — this decouples the two. The easing is solved against
       elapsed time, so it behaves the same at 60, 90 or 120 Hz.
       -------------------------------------------------------------------- */
    var KEYS = ['p', 'rain', 'reveal', 'gw', 'press', 'lift'];
    var cur = { p: 0, rain: 0, reveal: 0, gw: 0, press: 0, lift: 0 };
    var tgt = { p: 0, rain: 0, reveal: 0, gw: 0, press: 0, lift: 0 };
    var so = [];        /* per-stage opacity, also eased */
    var st = [];
    var lit = [];
    /* -1, not the real starting opacity. The per-frame write is guarded on
       `o !== so[i]`, so seeding so[0] with 1 — which is exactly what stage 0
       computes on the first frame — skipped the only write that mattered and
       left --pbd-o unset, which the CSS reads as opacity 0. The opening
       headline never appeared. */
    for (var i = 0; i < stages.length; i++) { so.push(-1); st.push(0); lit.push(-1); }

    /* Stage windows: a long plateau with a soft shoulder at each end, so the
       copy dissolves rather than switching. */
    /* First and last windows run past the ends so the opening headline is up
       on load and the closing one does not dim as the hero hands off. */
    /* Widened 2026-08-14: the middle stages were mostly fade, barely any
       plateau — (b-a) - 2*FADE is the time actually spent fully readable,
       and at the old 0.16-wide windows with FADE 0.055 that plateau was
       only ~0.05, a blink at normal scroll speed. Wider windows plus a
       narrower fade give each headline real time on screen; track_length's
       schema default went up to match (see the section file) so the extra
       p-width is also extra physical scroll distance, not just a smaller
       fraction of the same distance. */
    var EDGES = [-0.05, 0.17, 0.35, 0.52, 0.68, 0.84, 1.3];
    var FADE = 0.035;

    function measure() {
      var rect = track.getBoundingClientRect();
      var travel = rect.height - pin.offsetHeight;
      if (travel <= 0) return;
      var p = clamp(-rect.top / travel, 0, 1);

      tgt.p = p;
      tgt.rain = pulse(p, 0.10, 0.27, 0.45, 0.67);
      tgt.reveal = ramp(p, 0.28, 0.52);
      tgt.gw = ramp(p, 0.46, 0.73);
      tgt.press = ramp(p, 0.62, 0.85);
      tgt.lift = ramp(p, 0.78, 0.97);

    }

    function windows(p) {
      for (var i = 0; i < stages.length; i++) {
        var a = EDGES[i], b = EDGES[i + 1];
        st[i] = clamp(Math.min((p - a) / FADE, (b - p) / FADE), 0, 1);
      }
      var best = 0, bi = 0;
      for (i = 0; i < st.length; i++) { if (st[i] > best) { best = st[i]; bi = i; } }
      if (best < 0.02) st[bi] = 1;
    }

    var last = 0;
    var live = false;
    var settled = false;
    var shown = {};     /* last value actually written to the DOM, per key */
    var rainOn = null;

    function tick(now) {
      if (!live) return;
      var dt = last ? Math.min(now - last, 60) : 16.7;
      last = now;
      /* per-frame factor for a ~110 ms time constant, solved against dt */
      var k = 1 - Math.pow(0.001, dt / 340);
      var moving = false;

      for (var i = 0; i < KEYS.length; i++) {
        var key = KEYS[i];
        var d = tgt[key] - cur[key];
        if (Math.abs(d) < 0.00035) {
          cur[key] = tgt[key];
        } else {
          cur[key] += d * k;
          moving = true;
        }
        /* Only write when the value the browser would see actually changes.
           Every setProperty on this element invalidates the style of everything
           under it, and most frames only two or three of these are moving. */
        var v = Math.round(cur[key] * 1e4) / 1e4;
        if (v !== shown[key]) {
          shown[key] = v;
          hero.style.setProperty('--pbd-' + key, v);
        }
      }

      /* The rain ripples animate continuously whether or not it is raining.
         Off-stage that is pure cost, so stand them down — one class toggle at
         the threshold, not per frame. */
      var wet = cur.rain > 0.02;
      if (wet !== rainOn) {
        rainOn = wet;
        hero.classList.toggle('is-dry', !wet);
      }

      if (cam) cam.draw(cur);

      windows(cur.p);
      for (i = 0; i < stages.length; i++) {
        var o = Math.round(st[i] * 1e3) / 1e3;
        if (o !== so[i]) {
          so[i] = o;
          stages[i].style.setProperty('--pbd-o', o);
        }
        var state = so[i] > 0.9 ? 2 : so[i] > 0.01 ? 1 : 0;
        if (state !== lit[i]) {
          lit[i] = state;
          stages[i].classList.toggle('is-lit', state === 2);
          stages[i].classList.toggle('is-off', state === 0);
        }
      }

      var idx = 0;
      for (i = EDGES.length - 2; i >= 0; i--) { if (cur.p >= EDGES[i]) { idx = i; break; } }
      for (i = 0; i < ticks.length; i++) ticks[i].classList.toggle('is-on', i <= idx);

      if (!moving) {
        if (settled) { live = false; last = 0; return; }
        settled = true;          /* one more frame, then stand down */
      } else {
        settled = false;
      }
      raf(tick);
    }

    function wake() {
      settled = false;
      if (live) return;
      live = true;
      last = 0;
      raf(tick);
    }

    /* The camera is created last, once wake() exists, so its "assets just
       finished loading" callback can ask for a fresh frame even if the
       spring had already settled (e.g. nobody has scrolled since page load). */
    var canvasEl = $('.pbd-hero__canvas', hero);
    var cam = canvasEl ? initCamera(hero, canvasEl, wake) : null;

    addScrollTask(function () { measure(); wake(); });
    addResizeTask(function () { measure(); wake(); });

    /* Keep the spring off entirely when the hero is nowhere near the viewport. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        if (es[0].isIntersecting) { measure(); wake(); }
      }, { rootMargin: '200px' }).observe(track);
    }
  }

  /* =========================================================================
     2. CHAPTER NAVIGATION — stuck state, scrollspy, mobile auto-centring
     ====================================================================== */
  function initChapters(nav) {
    var inner = $('.pbd-chapters__inner', nav);
    var links = $$('.pbd-chapters__link', nav);
    if (!links.length) return;

    var targets = links
      .map(function (a) {
        var id = a.getAttribute('href');
        if (!id || id.charAt(0) !== '#') return null;
        var el = document.getElementById(id.slice(1));
        return el ? { link: a, el: el } : null;
      })
      .filter(Boolean);

    /* A chapter that points at the page you are already on — pricing while on
       the pricing page — is marked once and then left alone by the scrollspy. */
    var here = location.pathname.replace(/\/$/, '');
    var onPage = [];
    links.forEach(function (a) {
      var href = a.getAttribute('href') || '';
      if (href.charAt(0) === '#' || /^https?:/i.test(href) && a.hostname !== location.hostname) return;
      var path = a.pathname ? a.pathname.replace(/\/$/, '') : '';
      if (path && here && path === here) {
        a.setAttribute('aria-current', 'page');
        onPage.push(a);
      }
    });

    var active = null;
    function setActive(entry) {
      if (active === entry) return;
      active = entry;
      links.forEach(function (a) {
        if (onPage.indexOf(a) === -1) a.removeAttribute('aria-current');
      });
      if (!entry) return;
      entry.link.setAttribute('aria-current', 'true');
      /* Keep the current chapter visible in the mobile scroller */
      if (inner && inner.scrollWidth > inner.clientWidth) {
        var l = entry.link;
        var want = l.offsetLeft - (inner.clientWidth - l.offsetWidth) / 2;
        if (inner.scrollTo) {
          inner.scrollTo({ left: want, behavior: reduce.matches ? 'auto' : 'smooth' });
        } else {
          inner.scrollLeft = want;
        }
      }
    }

    addScrollTask(function () {
      nav.classList.toggle('is-stuck', nav.getBoundingClientRect().top <= (parseFloat(getComputedStyle(nav).top) || 0) + 1);

      var line = window.innerHeight * 0.34;
      var found = null;
      for (var i = 0; i < targets.length; i++) {
        if (targets[i].el.getBoundingClientRect().top <= line) found = targets[i];
      }
      setActive(found);
    });
  }

  /* =========================================================================
     3. REVEALS — content, diagrams, process rails
     ====================================================================== */
  function initReveals(root) {
    var items = $$('[data-pbd-in], [data-pbd-reveal], .pbd-process', root);
    if (!items.length) return;

    /* Pre-measure every drawn path so the dash animation is exact. */
    $$('.pbd-r-draw', root).forEach(function (path) {
      try {
        var len = path.getTotalLength();
        if (len) path.style.setProperty('--pbd-len', Math.ceil(len));
      } catch (e) { /* non-path element — ignore */ }
    });

    if (reduce.matches || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });

    items.forEach(function (el) { io.observe(el); });

    /* Stagger children that opt in via data-pbd-stagger */
    $$('[data-pbd-stagger]', root).forEach(function (group) {
      var step = parseFloat(group.getAttribute('data-pbd-stagger')) || 0.08;
      Array.prototype.forEach.call(group.children, function (child, i) {
        child.style.setProperty('--pbd-d', (i * step).toFixed(2) + 's');
      });
    });
  }

  /* =========================================================================
     3b. FIGURE KEYS
     The numbered markers on each diagram used to be keyed to a printed list
     underneath. The label now lives on the marker: hover, tap or tab one and
     the caption line under the figure says what it is. The full list is still
     in the DOM, visually hidden, so nothing is lost to a screen reader.
     ====================================================================== */
  function initFigKeys(root) {
    $$('.pbd-fig', root).forEach(function (fig) {
      var pins = $$('.pbd-fkey__pin', fig);
      if (!pins.length) return;
      /* the caption is a sibling of the figure, not a child of it */
      var cap = fig.parentNode && $('[data-pbd-figcap]', fig.parentNode);
      if (!cap) return;
      var text = $('[data-pbd-figtext]', cap);
      var num = $('[data-pbd-fignum]', cap);
      var base = text.getAttribute('data-default') || text.textContent;

      function show(pin) {
        text.textContent = pin.getAttribute('data-key');
        num.textContent = pin.getAttribute('data-n');
        cap.classList.add('is-keyed');
      }
      function clear() {
        text.textContent = base;
        num.textContent = '';
        cap.classList.remove('is-keyed');
      }

      pins.forEach(function (pin) {
        pin.addEventListener('pointerenter', function () { show(pin); });
        pin.addEventListener('pointerleave', clear);
        pin.addEventListener('focus', function () { show(pin); });
        pin.addEventListener('blur', clear);
        /* touch: pointerenter fires but pointerleave may not, so a tap
           anywhere else in the figure puts the caption back */
        pin.addEventListener('click', function (e) { e.preventDefault(); show(pin); });
      });
      fig.addEventListener('pointerleave', clear);
    });
  }

  /* =========================================================================
     4. FAQ — native <details> with a smooth height transition
     ====================================================================== */
  function initFaq(root) {
    $$('.pbd-faq', root).forEach(function (list) {
      var single = list.hasAttribute('data-pbd-single');
      var items = $$('.pbd-faq__item', list);

      items.forEach(function (item) {
        var panel = $('.pbd-faq__a', item);
        var summary = $('.pbd-faq__q', item);
        if (!panel || !summary) return;

        /* Start from the correct height so an item open on load stays open. */
        panel.style.height = item.open ? 'auto' : '0px';

        function animate(open) {
          if (reduce.matches) {
            panel.style.height = open ? 'auto' : '0px';
            return;
          }
          var start = panel.offsetHeight;
          panel.style.height = start + 'px';
          var end = open ? panel.firstElementChild.offsetHeight : 0;
          panel.style.transition = 'height .55s cubic-bezier(.16,1,.3,1)';
          raf(function () {
            raf(function () { panel.style.height = end + 'px'; });
          });
          var done = function (e) {
            if (e.propertyName !== 'height') return;
            panel.removeEventListener('transitionend', done);
            panel.style.transition = '';
            if (open) panel.style.height = 'auto';
          };
          panel.addEventListener('transitionend', done);
        }

        summary.addEventListener('click', function (e) {
          e.preventDefault();
          var opening = !item.open;

          if (opening && single) {
            items.forEach(function (other) {
              if (other === item || !other.open) return;
              var p = $('.pbd-faq__a', other);
              other.open = false;
              if (p) { p.style.height = p.offsetHeight + 'px'; raf(function () { p.style.transition = 'height .45s cubic-bezier(.16,1,.3,1)'; p.style.height = '0px'; }); }
            });
          }

          if (opening) {
            item.open = true;
            animate(true);
          } else {
            animate(false);
            var close = function (ev) {
              if (ev.propertyName !== 'height') return;
              panel.removeEventListener('transitionend', close);
              item.open = false;
            };
            if (reduce.matches) { item.open = false; }
            else { panel.addEventListener('transitionend', close); }
          }
        });
      });
    });
  }

  /* =========================================================================
     5. BOOT
     ====================================================================== */
  function boot() {
    var roots = $$(ROOT_SEL);
    if (!roots.length) return;

    roots.forEach(function (root) {
      root.setAttribute('data-pbd-js', '');
      $$('.pbd-hero', root).forEach(initHero);
      $$('.pbd-chapters', root).forEach(initChapters);
      initReveals(root);
      initFigKeys(root);
      initFaq(root);
    });

    if (scrollTasks.length) {
      window.addEventListener('scroll', onScroll, { passive: true });
    }
    window.addEventListener('resize', function () {
      for (var i = 0; i < resizeTasks.length; i++) resizeTasks[i]();
      onScroll();
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify Theme Editor: re-run when a section is re-rendered or shown. */
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.querySelector(ROOT_SEL)) return;
    window.__pbDrainageInit = false;
    var s = document.createElement('script');
    s.textContent = '';
    boot();
  });
})();
